import React, { useEffect, useState } from 'react';
import { Layout, Typography, Row, Col, message, Modal } from 'antd';

import OrderService from 'services/order';
import ReturnService from 'services/return';

import { PageSpinner } from 'components/page-spinner';
import PdButton from 'components/pd-button';
import './style.scss';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import { get } from 'lodash';
import ActivityBar from 'components/activityBar';
import ModalReturnBankUpdate from 'components/modalReturnBankUpdate';
import ModalReturn from 'components/modalReturn';
import { useLocation, useNavigate } from 'react-router-dom';

const { Content } = Layout;
const { Title, Text } = Typography;

const FIELDS = {
  ID: 'id',
  INVOICE_NUMBER: 'invoice_number',
  STATUS: 'status',
  ORDERED_AT: 'ordered_at',
  USER: 'user',
  ADDRESS: 'address',
  COURIER: 'courier',
  SHIPPING_METHOD: 'shipping_method',
  ORDER_PRODUCTS: 'order_products',
  TOTAL_PRICE: 'total_price',
  SHIPPING_COST: 'shipping_cost',
  NET_PRICE: 'net_price',
  TRACKING_CODE: 'tracking_code',
};

const ADDRESS_FIELDS = {
  ADDRESS: 'address',
  SECOND_LINE_ADDRESS: 'second_line_address',
  CITY: 'city',
  PROVINCE: 'province',
  ZIP_CODE: 'zip_code',
  NOTES: 'notes',
  RECIPIENT: 'recipient',
  PHONE_NUMBER: 'phone_number',
};

const USER_FIELDS = {
  NAME: 'first_name',
  PHONE_NUMBER: 'phone_number',
};

const COURIER_FIELDS = {
  TITLE: 'title',
};

const ACTIVITY_FIELDS = {
  ACTOR: 'actor',
  VERB: 'verb',
  CREATED_AT: 'created_at',
  MESSAGE: 'message',
};

const ACTOR_FIELDS = {
  FULL_NAME: 'full_name',
};

export function DisbursementDetail() {
  const navigate = useNavigate();
  const [value, setValue] = useState({});
  const [orderActivity, setOrderActivity] = useState([]);
  const [returnActivity, setReturnActivity] = useState([]);
  const [returnDetail, setReturnDetail] = useState([]);
  const [modalType, setModalType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalImages, setModalImages] = useState({});
  const [loadingPage, setLoadingPage] = useState(true);
  const [modalReturnBankUpdateVisible, setModalReturnBankUpdateVisible] =
    useState(false);
  const [modalReturnFundVisible, setModalReturnFundVisible] = useState(false);

  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const id = urlParams.get('id');

  function formatStatus(str) {
    str = str.split('_');
    str = str.map((item) => {
      return item.charAt(0).toUpperCase() + item.slice(1);
    });
    return str.join(' ');
  }

  function formatDate(str) {
    return dayjs(str).format('DD/MM/YYYY');
  }

  function formatTime(str) {
    return dayjs(str).format('LT');
  }

  function formatMessage(str) {
    if (str) {
      return 'Note: ' + str.charAt(0).toUpperCase() + str.slice(1);
    }
  }

  function formatAddress(obj) {
    let address = `${get(obj, ADDRESS_FIELDS.CITY, '')}, ${get(
      obj,
      ADDRESS_FIELDS.PROVINCE,
      ''
    )} ${get(obj, ADDRESS_FIELDS.ZIP_CODE, '')}`;
    return address;
  }

  async function getOrder() {
    try {
      setLoadingPage(true);
      if (id) {
        const { data } = await OrderService.getOrderDetail(id);
        const orderDetail = data.data;
        setValue(orderDetail);
        const orderActivity = await OrderService.getOrderActivity(id);
        setOrderActivity(orderActivity.data.data);
        const returnActivity = await ReturnService.getOrderActivity(id);
        setReturnActivity(get(returnActivity, 'data.data.activities', []));
        setReturnDetail(get(returnActivity, 'data.data.detail', {}));
      }
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      setLoadingPage(false);
    }
  }

  function handleReturn() {
    setModalReturnFundVisible(true);
    setShowModal(false);
  }

  function handleUpdateBank() {
    setModalReturnBankUpdateVisible(true);
    setShowModal(false);
  }

  function cancelModal() {
    setShowModal(false);
  }

  function handleModalFundAction() {
    setModalReturnFundVisible(false);
    setShowModal(false);
  }

  useEffect(() => {
    getOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!id) {
      navigate({
        pathname: '/shipments',
      });
    }
  }, [navigate, id]);

  if (loadingPage) {
    return <PageSpinner />;
  }

  function showTrackModal() {
    setModalType('track');
    setShowModal(true);
  }

  function handleImageModal(path) {
    setModalImages({
      visible: true,
      image: path,
    });
  }

  function cancelModalImages() {
    setModalImages({
      visible: false,
      images: '',
    });
  }

  const trackingNumber = get(value, FIELDS.TRACKING_CODE, '');

  const returnModalComponent = (
    <>
      <div className="modal-header">
        <h1>Return Request</h1>
      </div>
      <div className="modal-body">
        {returnActivity.map((track, idx) => (
          <div key={idx}>
            <Row>
              <Col
                className={`${idx !== 0 ? 'top-divider' : ''}`}
                offset={7}
                lg={17}></Col>
            </Row>
            <div key={track.id} className={`activity-row`}>
              <ActivityBar
                actor={track.actor.full_name}
                desc={track.message}
                date={track.created_at}
                msgData={track.message_data}
                dataType={track.verb}
              />

              {track.verb === 'create_return' && track.images.length > 0 && (
                <div>
                  <Row>
                    <Col offset={7} className="pl-5">
                      Images:
                    </Col>
                  </Row>
                  <Row className="imageBar">
                    {track.images.map((path, idx) => (
                      <Col key={idx} offset={idx === 0 ? 7 : 0} lg={4}>
                        <img
                          src={path}
                          alt="img"
                          onClick={() => handleImageModal(path)}
                        />
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
              {idx === 0 &&
                ((returnActivity[idx].verb === 'update_bank' &&
                  returnActivity[0].verb === 'update_bank') ||
                  (returnActivity[idx].verb === 'return_failed' &&
                    returnActivity[0].verb === 'return_failed')) && (
                  <>
                    <Row>
                      <Col lg={12} offset={7} className="button-group">
                        <PdButton
                          onClick={handleReturn}
                          className="button-return"
                          type="primary">
                          Return Fund
                        </PdButton>
                        <PdButton
                          onClick={handleUpdateBank}
                          outline
                          className="button-return"
                          type="primary">
                          Update Bank Account
                        </PdButton>
                      </Col>
                    </Row>
                  </>
                )}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <Layout className="pd-cms-shipments main-container">
      <div className="content">
        <div className="pageTitle">Disbursements Detail</div>
        <Content>
          <Modal
            className="modal"
            open={showModal}
            onCancel={() => cancelModal()}
            footer={null}
            width={800}>
            {modalType === 'track' && returnModalComponent}
          </Modal>
          {(returnActivity[0].verb === 'update_bank' ||
            returnActivity[0].verb === 'return_failed') && (
            <>
              <ModalReturnBankUpdate
                order={value}
                onOk={() => {
                  setModalReturnBankUpdateVisible(false);
                  setShowModal(false);
                }}
                bankDetail={returnDetail}
                customerName={`${get(value, 'user.first_name')} ${get(
                  value,
                  'user.last_name'
                )}`}
                open={modalReturnBankUpdateVisible}
                onCancel={() => {
                  setShowModal(false);
                  setModalReturnBankUpdateVisible(false);
                }}
                onSubmitSuccess={(returnData) => {
                  setReturnActivity(
                    get(returnData, 'data.data.activities', [])
                  );
                  setModalReturnBankUpdateVisible(false);
                  showTrackModal();
                }}
              />
              <ModalReturn
                orderDetail={value}
                onOK={handleModalFundAction}
                onCancel={handleModalFundAction}
                open={modalReturnFundVisible}
                onSubmitSuccess={(returnData) => {
                  setReturnActivity(
                    get(returnData, 'data.data.activities', [])
                  );
                  setModalReturnFundVisible(false);
                  showTrackModal();
                }}
              />
            </>
          )}

          <Row>
            <Col span={24} className="section-container">
              <div className=" inner-section">
                <div className="summaryContent">
                  <Row>
                    <Col span={24}>
                      <Title className="pageSubtitle" level={3}>
                        Order Summary
                      </Title>
                    </Col>
                    <Col span={18}>
                      <Row>
                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong className="">
                              Invoice Number
                            </Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">
                              {get(value, FIELDS.INVOICE_NUMBER, '')}
                            </Text>
                          </Col>
                        </Col>

                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong className="">
                              Order No.
                            </Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">
                              {get(value, FIELDS.ID, '')}
                            </Text>
                          </Col>
                        </Col>

                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong>Status</Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">
                              {formatStatus(get(value, FIELDS.STATUS, ''))}
                            </Text>
                          </Col>
                        </Col>

                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong>Transaction Date</Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">
                              {formatDate(get(value, FIELDS.ORDERED_AT, ''))}
                            </Text>
                          </Col>
                        </Col>

                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong>Customer Name</Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">
                              {get(
                                get(value, FIELDS.USER, {}),
                                USER_FIELDS.NAME,
                                ''
                              )}
                            </Text>
                          </Col>
                        </Col>

                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong>Customer Phone</Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">
                              {get(
                                get(value, FIELDS.USER, {}),
                                USER_FIELDS.PHONE_NUMBER,
                                ''
                              )}
                            </Text>
                          </Col>
                        </Col>

                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong>Recipient Name</Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">
                              {get(
                                get(value, FIELDS.ADDRESS, {}),
                                ADDRESS_FIELDS.RECIPIENT,
                                ''
                              )}
                            </Text>
                          </Col>
                        </Col>
                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong>Recipient Phone</Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">
                              {get(
                                get(value, FIELDS.ADDRESS, {}),
                                ADDRESS_FIELDS.PHONE_NUMBER,
                                ''
                              )}
                            </Text>
                          </Col>
                        </Col>

                        <Col span={24}>
                          <Col span={7} style={{ marginBottom: '0' }}>
                            <Text strong>Shipping Address</Text>
                          </Col>
                          <Col span={17} style={{ marginBottom: '0' }}>
                            <Text type="secondary">
                              {get(
                                get(value, FIELDS.ADDRESS, {}),
                                ADDRESS_FIELDS.ADDRESS,
                                ''
                              )}
                            </Text>
                          </Col>
                        </Col>
                        {get(value, FIELDS.ADDRESS, {})[
                          ADDRESS_FIELDS.SECOND_LINE_ADDRESS
                        ] && (
                          <Col span={24}>
                            <Col span={7} style={{ margin: '0' }}></Col>
                            <Col span={17} style={{ margin: '0' }}>
                              <Text type="secondary">
                                {get(
                                  get(value, FIELDS.ADDRESS, {}),
                                  ADDRESS_FIELDS.SECOND_LINE_ADDRESS,
                                  ''
                                )}
                              </Text>
                            </Col>
                          </Col>
                        )}

                        <Col span={24} className="mb-15">
                          <Col span={7} style={{ marginTop: '0' }}></Col>
                          <Col span={17} style={{ marginTop: '0' }}>
                            <Text type="secondary">
                              {formatAddress(get(value, FIELDS.ADDRESS, {}))}
                            </Text>
                          </Col>
                        </Col>

                        {get(value, FIELDS.USER, {})[ADDRESS_FIELDS.NOTES] && (
                          <Col span={24} className="mb-15">
                            <Col span={7}>
                              <Text strong>Shipping Note</Text>
                            </Col>
                            <Col span={17}>
                              <Text type="secondary">
                                {get(
                                  get(value, FIELDS.USER, {}),
                                  ADDRESS_FIELDS.NOTES,
                                  ''
                                )}
                              </Text>
                            </Col>
                          </Col>
                        )}

                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong>Courier</Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">
                              {get(
                                get(value, FIELDS.COURIER, {}),
                                COURIER_FIELDS.TITLE,
                                ''
                              )}
                            </Text>
                          </Col>
                        </Col>

                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong>Shipping Method</Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">
                              {get(value, FIELDS.SHIPPING_METHOD, '')}
                            </Text>
                          </Col>
                        </Col>

                        {trackingNumber && (
                          <Col span={24} className="mb-15">
                            <Col span={7}>
                              <Text strong>Tracking Number</Text>
                            </Col>
                            <Col span={17}>
                              <Text type="secondary">{trackingNumber}</Text>
                            </Col>
                          </Col>
                        )}
                      </Row>
                    </Col>
                    <Col span={6}>
                      <div className="buttonSection">
                        {(get(value, FIELDS.STATUS, '') === 'disburse_ready' ||
                          get(value, FIELDS.STATUS, '') ===
                            'disburse_pending') && (
                          <>
                            <PdButton
                              onClick={showTrackModal}
                              outline
                              className="button-detail"
                              type="primary">
                              View Request
                            </PdButton>
                          </>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>

            <Col span={24} className="section-container">
              <div className="inner-section">
                <Title className="pageSubtitle" level={3}>
                  Products
                </Title>
                {get(value, FIELDS.ORDER_PRODUCTS, []).map((op, index) => (
                  <div key={index} className="product-list">
                    <Col span={4}>
                      <img
                        src={op.product.preview.secure_url}
                        alt={op.product.preview.title}
                        className="scale-img"
                      />
                    </Col>
                    <Col span={15}>
                      <div>
                        <Text strong>{op.product.title}</Text>
                      </div>
                      <div className="fs-14">
                        <Text>
                          {op.quantity}{' '}
                          {op.quantity > 1 ? 'products' : 'product'}
                        </Text>
                      </div>
                      {typeof get(op.color, 'color') !== 'undefined' && (
                        <div className="fs-14">
                          <Text>Color: {get(op.color, 'color')}</Text>
                        </div>
                      )}
                    </Col>
                    <Col span={4}>
                      <Text strong>{op.quantity} pcs</Text>
                    </Col>
                  </div>
                ))}
              </div>
            </Col>

            <Col span={24} className="section-container">
              <div className="inner-section">
                <Title className="pageSubtitle" level={3}>
                  Order Status
                </Title>
                {orderActivity.map((activity, index) => (
                  <div key={index} className="activityContent">
                    <Col span={5}>
                      {get(
                        get(activity, ACTIVITY_FIELDS.ACTOR, {}),
                        ACTOR_FIELDS.FULL_NAME,
                        ''
                      ) === 'system' ? (
                        <div className="activity activity-system">
                          {formatStatus(
                            get(
                              get(activity, ACTIVITY_FIELDS.ACTOR, {}),
                              ACTOR_FIELDS.FULL_NAME,
                              ''
                            )
                          )}
                        </div>
                      ) : get(
                          get(activity, ACTIVITY_FIELDS.ACTOR, {}),
                          ACTOR_FIELDS.FULL_NAME,
                          ''
                        ) === 'customer' ? (
                        <div className="activity activity-customer">
                          {formatStatus(
                            get(
                              get(activity, ACTIVITY_FIELDS.ACTOR, {}),
                              ACTOR_FIELDS.FULL_NAME,
                              ''
                            )
                          )}
                        </div>
                      ) : (
                        <div className="activity activity-admin">
                          {formatStatus(
                            get(
                              get(activity, ACTIVITY_FIELDS.ACTOR, {}),
                              ACTOR_FIELDS.FULL_NAME,
                              ''
                            )
                          )}
                        </div>
                      )}
                    </Col>
                    <Col span={10} offset={1}>
                      {formatStatus(get(activity, ACTIVITY_FIELDS.VERB, ''))}
                      <br />
                      {formatMessage(
                        get(activity, ACTIVITY_FIELDS.MESSAGE, '')
                      )}
                    </Col>
                    <Col span={10} className="fs-14">
                      {formatDate(
                        get(activity, ACTIVITY_FIELDS.CREATED_AT, '')
                      )}{' '}
                      -{' '}
                      {formatTime(
                        get(activity, ACTIVITY_FIELDS.CREATED_AT, '')
                      )}
                    </Col>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Content>
      </div>
      <Modal
        className="modal-image"
        open={modalImages.visible}
        onCancel={() => cancelModalImages()}
        footer={null}
        width={800}>
        <Row>
          <Col>
            <img src={modalImages.image} alt="img" />
          </Col>
        </Row>
      </Modal>
    </Layout>
  );
}

export default { DisbursementDetail };
